import { motion } from "framer-motion";
import { RevealLineOnView } from "./RevealLine";

const chapters = [
  {
    n: "01",
    title: "Assessment",
    body: "Every vehicle begins with an unhurried inspection. Paint condition, contamination, existing defects — nothing skipped.",
  },
  {
    n: "02",
    title: "Decontamination",
    body: "A pre-wash foam bath and iron-fallout treatment lift road grime, tar and bonded contaminants before touch.",
  },
  {
    n: "03",
    title: "Hand Wash",
    body: "A two-bucket hand wash with plush microfibre — never brushes. Panels dried with edgeless towels.",
  },
  {
    n: "04",
    title: "Correction",
    optional: true,
    body: "Machine polishing removes swirls, holograms and light scratches. Depth is restored, gloss is elevated.",
  },
  {
    n: "05",
    title: "Interior",
    body: "Extraction vacuuming through every seat and floor. Steam-clean plastics, dressed leather, sanitised touchpoints.",
  },
  {
    n: "06",
    title: "Protection",
    optional: true,
    body: "Optional 10H nano-graphene ceramic coating — up to five years of gloss, hydrophobia and defence.",
  },
  {
    n: "07",
    title: "Final Reveal",
    body: "A curated inspection under both natural and studio light. Delivered like it left the factory. Or better.",
  },
];

export default function Manifesto() {
  return (
    <section id="process" className="py-28 md:py-40 bg-[#050505]">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mb-20 md:mb-32">
          <div className="md:col-span-5">
            <p className="eyebrow mb-6">
              <RevealLineOnView>The Manifesto — 01 / 07</RevealLineOnView>
            </p>
            <h2 className="font-serif text-5xl md:text-7xl font-light tracking-tight text-white leading-[0.95]">
              <RevealLineOnView>Seven</RevealLineOnView>
              <RevealLineOnView delay={0.08} className="italic text-neutral-400">
                chapters of care.
              </RevealLineOnView>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 md:pt-10">
            <p className="text-neutral-400 text-base md:text-lg leading-relaxed max-w-lg">
              At SEVEN7H, detailing is not a checklist — it is a discipline.
              Seven measured chapters, followed on every vehicle, in the same
              order, for the same reason: obsessive, uncompromising results.
            </p>
          </div>
        </div>

        <div className="border-t border-white/10">
          {chapters.map((c, i) => (
            <motion.div
              key={c.n}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-12 gap-4 md:gap-8 py-8 md:py-12 border-b border-white/10 group"
            >
              <div className="col-span-2 md:col-span-1 font-mono text-neutral-500 text-xs md:text-sm pt-2">
                {c.n}
              </div>
              <h3 className="col-span-10 md:col-span-4 font-serif text-3xl md:text-5xl font-light text-white group-hover:italic transition-all duration-500">
                {c.title}
                {c.optional && (
                  <span className="block md:inline md:ml-3 font-mono text-[10px] md:text-xs tracking-[0.24em] uppercase text-brand-red not-italic align-middle">
                    (Optional Extra)
                  </span>
                )}
              </h3>
              <p className="col-start-3 md:col-start-7 col-span-10 md:col-span-6 text-neutral-400 text-sm md:text-base leading-relaxed max-w-lg">
                {c.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
